import {SimulationsLayout} from "@/components/layouts/page/SimulationsLayout";

import styles from "./styles.module.scss";

// ── Static course groups ─────────────────────────────────────────
// Two static groups (courses), each with a single square that is itself
// the button. The previous data-driven implementation (fetch scenarios,
// group by product_line, real scenario ids) is preserved in the commented
// block at the bottom of this file. Restore it to go back to the dynamic
// scheme.
export const CallPage = () => {
  // Each group is a course. Its square is one clickable button labelled with
  // the course title — no easy/medium/hard levels.
  //
  // These simulations have NO real scenario: the card `id` is a FAKE scenario
  // id (the ML side is told it is fake). What actually routes the session is
  // `course` — it selects the ML path prefix /certification_event/<course>/…:
  //   feedback → the "give feedback" course
  //   copilot  → the "work with MS Copilot" course
  // `value` is the tag ("easy" placeholder). Both ride to the chat via router
  // state (level object) and are sent on session create.
  const groups = [
    {
      id: "leadership",
      name: "Leadership Skills",
      label: "Giving Effective Feedback",
      course: "feedback",
    },
    {
      id: "tech",
      name: "Tech Skills",
      label: "Working with Microsoft Copilot",
      course: "copilot",
    },
  ];

  return (
    <div className={"container-big"}>
      <div className={styles.call}>
        <ul>
          {groups.map((group) => (
            <li key={group.id}>
              <SimulationsLayout
                asButton
                title={group.name}
                cards={[{id: `${group.id}-1`, name: group.label}]}
                levelList={[
                  {
                    id: 0,
                    type: "scenario",
                    value: "easy",
                    course: group.course,
                    remainingTime: 600000,
                  },
                ]}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ── Previous data-driven implementation (kept for easy restore) ──────
   To return to the dynamic scheme: delete the static CallPage above and
   uncomment this block, and restore the matching imports at the top:

     import {useEffect, useMemo, useState} from "react";
     import {useLevelOrder} from "@hooks/LevelOrder";
     import {clearTable, getAllScenarios} from "@redux/slices/scenarioSlices/tableSlice";
     import {BorderArticle} from "@ui/general/BorderArticle";
     import {Spinner} from "@ui/general/Spinner";
     import {WarningData} from "@ui/general/WarningData";
     import moment from "moment";
     import {useTranslation} from "react-i18next";
     import {useDispatch, useSelector} from "react-redux";

export const CallPage = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {certificationLevels} = useLevelOrder();

  // One square = one "Start" button instead of the easy/medium/hard trio.
  const startButton = [{id: 0, type: "scenario", value: "easy", name: "Start"}];

  const [scenariosToDisplay, setScenariosToDisplay] = useState([]);

  const {status} = useSelector((state) => state.scenario.table);
  const {scenarios} = useSelector((state) => state.scenario.table.data);
  const {certifications} = useSelector((state) => state.scenario.table.data);

  const isDueDatePassed = (due_date) => {
    if (!due_date) {
      return false;
    }

    const now = moment();
    const dueDate = moment(due_date);
    const diffDays = dueDate.diff(now, "days");

    return diffDays < -14;
  };

  const groupedScenarios = useMemo(() => {
    if (!scenarios || scenarios.length === 0) {
      return null;
    }

    return scenarios.reduce((groups, scenario) => {
      const productLine = scenario.product_line;

      if (!groups[productLine]) {
        groups[productLine] = [];
      }

      groups[productLine].push(scenario);

      return groups;
    }, {});
  }, [scenarios]);

  const isLoading = status === "loading";
  const isError = status === "error";
  const isEmpty = scenariosToDisplay.length === 0;

  useEffect(() => {
    if (groupedScenarios) {
      setScenariosToDisplay(
        Object.entries(groupedScenarios)
          .map(([productLine, scenarios]) => ({
            id: productLine,
            name: productLine,
            scenarios: scenarios.filter(
              (scenario) => !isDueDatePassed(scenario.due_date),
            ),
          }))
          .filter((group) => group.scenarios.length > 0)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    } else {
      setScenariosToDisplay([]);
    }
  }, [groupedScenarios]);

  useEffect(() => {
    dispatch(getAllScenarios());

    return () => {
      dispatch(clearTable());
    };
  }, [dispatch]);

  return (
    <div className={"container-big"}>
      <div className={styles.call}>
        {isLoading ? (
          <Spinner
            width={80}
            height={80}
          />
        ) : isError ? (
          <div className={styles.callEmpty}>
            <BorderArticle>
              <WarningData type={"error"} />
            </BorderArticle>
          </div>
        ) : isEmpty ? (
          <div className={styles.callEmpty}>
            <BorderArticle>
              <WarningData type={"empty"} />
            </BorderArticle>
          </div>
        ) : (
          <ul>
            {certifications && certifications.length > 0 && (
              <li>
                <SimulationsLayout
                  isBig
                  background={
                    "linear-gradient(135deg, #8692CB 0%, #B262C9 100%)"
                  }
                  levelList={certificationLevels}
                  cards={certifications.filter(
                    (certification) =>
                      !isDueDatePassed(certification.finish_date),
                  )}
                  title={
                    <span style={{color: "var(--white)"}}>
                      {t("page.simulations.tab.certification")}
                    </span>
                  }
                />
              </li>
            )}

            {scenariosToDisplay &&
              scenariosToDisplay.length > 0 &&
              scenariosToDisplay.map((item) => (
                <li key={item.id}>
                  <SimulationsLayout
                    title={item.name}
                    cards={item.scenarios}
                    levelList={startButton}
                  />
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
};
──────────────────────────────────────────────────────────────────── */
